import { Clock, HardDrive, type LucideIcon, MapPin, MemoryStick } from "lucide-react";
import type { ReactNode } from "react";

import type { DeviceDetail } from "@busright/data-contract";

import { relativeFromNow } from "@/lib/format";

// Device hardware/telemetry facts present on the /api/device payload: RAM, internal storage, and
// last-known location. Live battery/CPU/cellular-signal readings are NOT on this endpoint (they
// live on the Telemetry surface), so we show only what the contract carries — no fabricated metrics.

const MB_PER_GB = 1024;

const formatGb = (mb: number | null): string =>
  mb === null ? "—" : `${(mb / MB_PER_GB).toFixed(1)} GB`;

const formatCoord = (lat: number | null, lon: number | null): string =>
  lat === null || lon === null ? "—" : `${lat.toFixed(3)}, ${lon.toFixed(3)}`;

interface Reading {
  readonly label: string;
  readonly value: string;
  readonly icon: LucideIcon;
}

export interface DevTelemetryPanelProps {
  readonly device: DeviceDetail;
  // ISO instant the underlying data was last known fresh (envelope meta.dataAsOf).
  readonly dataAsOf: string | null;
}

export const DevTelemetryPanel = ({ device, dataAsOf }: DevTelemetryPanelProps): ReactNode => {
  const readings: readonly Reading[] = [
    { label: "Total RAM", value: formatGb(device.totalRamMb), icon: MemoryStick },
    { label: "Internal storage", value: formatGb(device.totalInternalStorageMb), icon: HardDrive },
    {
      label: "Last-known location",
      value: formatCoord(device.latitude, device.longitude),
      icon: MapPin,
    },
  ];

  return (
    <div>
      <div className="grid cols-3" style={{ gap: 12 }}>
        {readings.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.label}
              style={{
                padding: "12px 14px",
                background: "var(--br-surface-2)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  fontSize: 11,
                  color: "var(--br-text-muted)",
                }}
              >
                <Icon size={13} aria-hidden />
                {r.label}
              </div>
              <div
                className="br-tabular-nums"
                style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}
              >
                {r.value}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          marginTop: 12,
          fontSize: 11,
          color: "var(--br-text-muted)",
        }}
      >
        <Clock size={12} aria-hidden />
        As of last reading, {relativeFromNow(dataAsOf)} — live battery / CPU / cellular telemetry is
        on the Telemetry surface.
      </div>
    </div>
  );
};
